import type { DeviceFacts } from './contracts';

type Product = NonNullable<DeviceFacts['security']['antivirus']>[number];
export type ProtectionState = 'protected' | 'off' | 'unknown';
const STARTUP_GRACE = 5 * 60_000;

/** Legacy agents have no initialization mode. Judge their boot grace at inventory time, never at a later metrics report. */
export function antivirusProductState(product: Product, bootAt: string | null, observedAt: number): ProtectionState {
  if (product.mode === 'initializing' || product.mode === 'unknown') return 'unknown';
  if (product.mode === 'passive') return 'off'; // Passive Defender is not the primary antivirus.
  if (product.mode === 'active' && product.realTimeProtection === false) return 'off'; // A definite native negative needs no guessed enabled flag.
  // Native Defender's active *mode* does not establish real-time protection when that native flag was unreadable.
  // Security Center-only products/legacy agents still use their positive registered-enabled state.
  if (product.mode === 'active' && product.enabled === true && product.realTimeProtection === null) return 'unknown';
  if (product.enabled === true && product.realTimeProtection !== false) return 'protected';
  if (product.enabled === null) return 'unknown';
  const uptime = bootAt ? observedAt - Date.parse(bootAt) : NaN;
  if (product.mode === undefined && /defender/i.test(product.name) && uptime >= 0 && uptime < STARTUP_GRACE) return 'unknown';
  return 'off';
}

/** An active alternative wins; an uncertain product cannot establish either protection or an all-products-off fault. */
export function antivirusProtection(facts: DeviceFacts, observedAt: number) {
  const products = facts.security.antivirus;
  const protecting = products?.filter(p => antivirusProductState(p, facts.bootAt, observedAt) === 'protected') ?? [];
  const state: ProtectionState = protecting.length ? 'protected'
    : products === null || products.some(p => antivirusProductState(p, facts.bootAt, observedAt) === 'unknown') ? 'unknown' : 'off';
  return { state, protecting };
}
